// The venue's resources, as getResources returns them - the only complete copy.
//
// appconfig.resources (embedded in the init doc) must NOT be trusted for this. For ramu- venues
// falcon's configsync.js `syncResources` trigger copies a resource into the init doc only when that
// resource is written, so the embedded map holds whatever happened to be written since - at ramuclub
// two lockers and no courts - and leaves out group members. Treating its mere presence as "loaded"
// skipped getResources, so formatVacancies matched no court and the calendar showed no free times
// at all. It is used here only if getResources itself fails.
//
// Screens that format free times must wait for `resources` (null until then): they cache each
// formatted day, so a day formatted from a partial map would stay wrong after the full one came.
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useUiGet } from './useUiGet'

// One request per account however many components ask at once (App and the calendar mount together).
// Dropped once settled, so a later mount after a failure tries again.
const inflight = {}
const fetchOnce = (uiGet, accountkey) => {
  if (!inflight[accountkey]) {
    inflight[accountkey] = uiGet({ q: 'getResources', customerid: accountkey }).finally(() => {
      delete inflight[accountkey]
    })
  }
  return inflight[accountkey]
}

export function useVenueResources({ appconfig }) {
  const storeResources = useSelector((state) => state.resources && state.resources.resources)
  const dispatch = useDispatch()
  const uiGet = useUiGet({ appconfig })
  const [failed, setFailed] = useState(false)
  const accountkey = appconfig && appconfig.accountkey

  useEffect(() => {
    if (storeResources || !accountkey) return
    let alive = true
    fetchOnce(uiGet, accountkey)
      .then((response) => {
        if (!alive) return
        const data = response && response.data
        if (!data || data.error) {
          setFailed(true)
          return
        }
        dispatch({ type: 'UPDATE_RESOURCES', payload: data })
      })
      .catch(() => alive && setFailed(true))
    return () => {
      alive = false
    }
    // uiGet is rebuilt every render; the account and whether the store has the map are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountkey, !!storeResources])

  if (storeResources) return { resources: storeResources, failed: false }
  if (failed) return { resources: (appconfig && appconfig.resources) || null, failed: true }
  return { resources: null, failed: false }
}
